/**
 * P3-23/24: WorkshopTab —— Workshop tab 完整迁移。
 *
 * 迁移自 `com.nuclearunicorn.game.ui.tab.Workshop`（js/workshop.js:3138-3373）。
 *
 * 范围：
 * - craft effectiveness 头部、"Hide researched" 复选框；
 * - UpgradePanel：全部 workshop upgrades（UpgradeButton）；
 * - CraftPanel：craft 按钮列表、craft counts 表、consumption 表、空闲工程师显示；
 * - ZebraUpgradePanel：zebraWorkshop > 0 时显示 zebra upgrades。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { Panel } from "../../components/Panel";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { CraftButton } from "./CraftButton";
import { UpgradeButton } from "./UpgradeButton";
import { ZebraUpgradeButton } from "./ZebraUpgradeButton";

interface CraftCountRow {
  title: string;
  value: string;
}

interface WorkshopSnapshot {
  effectiveness: string;
  hideResearched: boolean;
  constructionResearched: boolean;
  mechanizationResearched: boolean;
  freeEngineers: number;
  totalEngineers: number;
  upgrades: string[];
  crafts: string[];
  craftCounts: CraftCountRow[];
  consumption: CraftCountRow[];
  zebraWorkshopBuilt: boolean;
  zebraUpgrades: string[];
}

function computeSnapshot(game: GamePage | null): WorkshopSnapshot | null {
  if (!game) {
    return null;
  }
  const workshop = game.workshop as {
    upgrades: { name: string }[];
    crafts: { name: string }[];
    zebraUpgrades: { name: string }[];
    hideResearched: boolean;
    getConsumptionEngineers: () => Record<string, number>;
  };
  const science = game.science as { get?: (name: string) => { researched?: boolean } };
  const constructionResearched = !!science.get?.("construction")?.researched;
  const mechanizationResearched = !!science.get?.("mechanization")?.researched;

  const village = game.village as {
    getFreeEngineers: () => number;
    getWorkerKittens: (job: string) => number;
  };

  const craftCounts: CraftCountRow[] = [];
  const resources = (game.resPool as {
    resources: { name: string; title?: string; craftable?: boolean; value: number }[];
  }).resources;
  for (const res of resources) {
    if (res.craftable && res.value) {
      craftCounts.push({
        title: res.title || res.name,
        value: game.getDisplayValueExt(res.value),
      });
    }
  }

  const consumption: CraftCountRow[] = [];
  const engineerConsumption = workshop.getConsumptionEngineers();
  for (const res of resources) {
    if (engineerConsumption[res.name]) {
      consumption.push({
        title: res.title || res.name,
        value: game.getDisplayValueExt(engineerConsumption[res.name], null, true),
      });
    }
  }

  const zebraWorkshop = (game.bld as {
    getBuildingExt?: (name: string) => { meta?: { val?: number } };
  }).getBuildingExt?.("zebraWorkshop");

  return {
    effectiveness: game.getDisplayValueExt(100 * game.getCraftRatio(), false, false, 0),
    hideResearched: workshop.hideResearched,
    constructionResearched,
    mechanizationResearched,
    freeEngineers: village.getFreeEngineers(),
    totalEngineers: village.getWorkerKittens("engineer"),
    upgrades: workshop.upgrades.map((u) => u.name),
    crafts: workshop.crafts.map((c) => c.name),
    craftCounts,
    consumption,
    zebraWorkshopBuilt: (zebraWorkshop?.meta?.val ?? 0) > 0,
    zebraUpgrades: workshop.zebraUpgrades.map((u) => u.name),
  };
}

function arraysEq(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function rowsEq(a: CraftCountRow[], b: CraftCountRow[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i].title !== b[i].title || a[i].value !== b[i].value) {
      return false;
    }
  }
  return true;
}

function snapshotEq(a: WorkshopSnapshot | null, b: WorkshopSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.effectiveness === b.effectiveness &&
    a.hideResearched === b.hideResearched &&
    a.constructionResearched === b.constructionResearched &&
    a.mechanizationResearched === b.mechanizationResearched &&
    a.freeEngineers === b.freeEngineers &&
    a.totalEngineers === b.totalEngineers &&
    a.zebraWorkshopBuilt === b.zebraWorkshopBuilt &&
    arraysEq(a.upgrades, b.upgrades) &&
    arraysEq(a.crafts, b.crafts) &&
    arraysEq(a.zebraUpgrades, b.zebraUpgrades) &&
    rowsEq(a.craftCounts, b.craftCounts) &&
    rowsEq(a.consumption, b.consumption)
  );
}

function CraftHeaderTooltip() {
  return (
    <span
      style={{ cursor: "pointer" }}
      title={t("workshop.craftPanel.header.tooltip")}
    >
      {t("workshop.craftPanel.header.freeEngineers")}
    </span>
  );
}

export default function WorkshopTab() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const toggleHideResearched = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    (game.workshop as { hideResearched: boolean }).hideResearched = !(
      game.workshop as { hideResearched: boolean }
    ).hideResearched;
  }, []);

  const craftPanel = useMemo(() => {
    if (!snap || !snap.constructionResearched) {
      return null;
    }
    return (
      <Panel title={t("workshop.craftPanel.label")}>
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td rowSpan={2}>
                {snap.mechanizationResearched && (
                  <div style={{ cursor: "pointer" }}>
                    <CraftHeaderTooltip />:{" "}
                    {snap.freeEngineers} / {snap.totalEngineers}
                  </div>
                )}
                {snap.crafts.map((name) => (
                  <CraftButton key={name} craftName={name} />
                ))}
              </td>
              <td className="craftStuffPanel" style={{ paddingLeft: "50px" }}>
                <div>{t("workshop.craftPanel.counts.label")}:</div>
                <table style={{ paddingTop: "20px" }}>
                  <tbody>
                    {snap.craftCounts.map((row) => (
                      <tr key={row.title}>
                        <td>{row.title}:</td>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
            {snap.consumption.length > 0 && (
              <tr>
                <td className="craftStuffPanel" style={{ paddingLeft: "50px" }}>
                  <div>{t("workshop.craftPanel.consumption.label")}:</div>
                  <table style={{ paddingTop: "20px" }}>
                    <tbody>
                      {snap.consumption.map((row) => (
                        <tr key={row.title}>
                          <td>{row.title}:</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>
    );
  }, [snap]);

  if (!snap) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Workshop">
        <div style={{ float: "left" }}>
          {t("workshop.craft.effectiveness", [snap.effectiveness])}
        </div>
        <div style={{ float: "right" }}>
          <input
            id="toggleResearched"
            type="checkbox"
            checked={snap.hideResearched}
            onChange={toggleHideResearched}
          />
          <label htmlFor="toggleResearched">{t("workshop.toggleResearched.label")}</label>
        </div>
        <div style={{ clear: "both" }} />

        <Panel title={t("workshop.upgradePanel.label")}>
          {snap.upgrades.map((name) => (
            <UpgradeButton key={name} upgradeName={name} />
          ))}
        </Panel>

        {craftPanel}

        {snap.zebraWorkshopBuilt && (
          <Panel title={t("workshop.zebraUpgradesPanel.label")}>
            {snap.zebraUpgrades.map((name) => (
              <ZebraUpgradeButton key={name} upgradeName={name} />
            ))}
          </Panel>
        )}
      </div>
    </TooltipProvider>
  );
}
